import { ArrowRight, BarChart3, Building2, CalendarDays, CheckCircle2, ClipboardCheck, FileText, Globe2, Layers3, Mail, MapPin, QrCode, ShieldCheck, UsersRound } from 'lucide-react';
import { CtaBand } from './components/CtaBand';
import { FeatureGrid } from './components/FeatureGrid';
import { Footer } from './components/Footer';
import { Header } from './components/Header';
import { Hero } from './components/Hero';
import { LogoStrip } from './components/LogoStrip';
import { PainPointSection } from './components/PainPointSection';
import { ProductGallery } from './components/ProductGallery';
import { Section } from './components/Section';
import { WorkflowSection } from './components/WorkflowSection';
import { caseStudies, channels, featureGroups, painPoints, proofMetrics, workflowSteps, type CaseStudy } from './content';

type AppRoute =
  | { name: 'home' }
  | { name: 'platform' }
  | { name: 'websites' }
  | { name: 'channels' }
  | { name: 'compliance' }
  | { name: 'workflow' }
  | { name: 'proof' }
  | { name: 'case-study'; slug: string }
  | { name: 'contact' };

export function App() {
  const route = currentRoute();

  return (
    <>
      <Header />
      <main>
        {route.name === 'home' ? <HomePage /> : null}
        {route.name === 'platform' ? <PlatformPage /> : null}
        {route.name === 'websites' ? <WebsitesPage /> : null}
        {route.name === 'channels' ? <ChannelsPage /> : null}
        {route.name === 'compliance' ? <CompliancePage /> : null}
        {route.name === 'workflow' ? <WorkflowPage /> : null}
        {route.name === 'proof' ? <ProofPage /> : null}
        {route.name === 'case-study' ? <CaseStudyPage slug={route.slug} /> : null}
        {route.name === 'contact' ? <ContactPage /> : null}
      </main>
      <Footer />
    </>
  );
}

function currentRoute(): AppRoute {
  const routes = ['platform', 'websites', 'channels', 'compliance', 'workflow', 'proof', 'contact'] as const;
  const pathname = window.location.pathname.replace(/^\/+|\/+$/g, '');
  if (routes.includes(pathname as (typeof routes)[number])) return { name: pathname as (typeof routes)[number] };

  if (pathname.startsWith('proof/')) {
    return { name: 'case-study', slug: pathname.replace(/^proof\//, '') };
  }

  const hashRoute = window.location.hash.replace(/^#/, '');
  if (routes.includes(hashRoute as (typeof routes)[number])) return { name: hashRoute as (typeof routes)[number] };

  return { name: 'home' };
}

function HomePage() {
  return (
    <>
      <Hero />
      <LogoStrip metrics={proofMetrics} />
      <PainPointSection items={painPoints} />
      <section className="page-directory">
        <div className="section-inner">
          <div className="section-heading compact">
            <p className="eyebrow">Choose the story</p>
            <h2>Explore the platform without scrolling through one long page.</h2>
          </div>
          <div className="directory-grid">
            {[
              ['Platform', '/platform', 'How B4 lines up campaigns, websites, members, and reports.'],
              ['Websites', '/websites', 'How B4 handles the website-builder job while keeping every signup connected.'],
              ['Channels', '/channels', 'How email, social, landing pages, QR, and signup forms work together.'],
              ['Compliance', '/compliance', 'How checklists make sure locations actually execute the plan.'],
              ['Workflow', '/workflow', 'How teams move from idea to launch without the fire drill.'],
              ['Product proof', '/proof', 'Screenshots of the workflow details B4 keeps visible.'],
            ].map(([title, href, description]) => (
              <a className="directory-card" href={href} key={href}>
                <strong>{title}</strong>
                <span>{description}</span>
                <ArrowRight size={18} />
              </a>
            ))}
          </div>
        </div>
      </section>
      <SharedCta />
    </>
  );
}

function PlatformPage() {
  return (
    <>
      <PageHero
        eyebrow="Platform"
        title="The campaign pieces finally line up."
        description="B4 replaces the tool pile that makes teams wonder which list is current, which page is live, which QR code was printed, and whether every location got the right offer."
      />
      <Section
        id="platform"
        eyebrow="The story B4 tells"
        title="Campaigns, websites, members, and reporting move together."
        description="The value is not another dashboard. It is knowing the list, offer, page, QR route, approval state, and results all belong to the same campaign."
      >
        <FeatureGrid features={featureGroups} />
      </Section>
      <Section
        id="confidence"
        className="confidence-section"
        eyebrow="Why clients feel the difference"
        title="Less guessing. Fewer apology emails. More confidence when the doors open."
        description="The system is designed around the moments that matter to clients: a guest scans the right code, joins the right list, gets the right offer, and the team can prove what happened."
      >
        <ConfidenceGrid />
      </Section>
      <Section
        id="capabilities"
        eyebrow="What teams stop worrying about"
        title="The features matter because they remove risk."
        description="B4 keeps brand rules, member data, campaign channels, public assets, and results connected."
      >
        <CapabilityList />
      </Section>
      <Section
        id="websites"
        className="confidence-section"
        eyebrow="Websites"
        title="A pretty website is not enough if every signup disappears into another tool."
        description="B4 covers the standalone site-builder job brands expect from tools like Squarespace, then connects the public experience to campaigns, QR codes, member records, locations, offers, and reporting."
      >
        <WebsiteProofGrid />
      </Section>
      <Section
        id="compliance"
        className="confidence-section"
        eyebrow="Compliance checklists"
        title="The campaign promise only works if every location executes it."
        description="Checklists give operations a repeatable way to confirm the real-world work behind a campaign: signage placed, offer rules understood, QR codes checked, staff briefed, and exceptions visible before they become customer problems."
      >
        <ComplianceGrid />
      </Section>
      <SharedCta />
    </>
  );
}

function WebsitesPage() {
  return (
    <>
      <PageHero
        eyebrow="Websites"
        title="Websites that do more than look good."
        description="B4 gives teams the public website and landing page experience they expect from a site builder, without creating another disconnected place where guest interest gets lost."
      />
      <Section
        id="website-pain"
        eyebrow="The real problem"
        title="Standalone websites often stop right when the relationship should begin."
        description="The emotional cost is not the page builder. It is the feeling that a guest raised their hand, scanned a code, or submitted a form, and the team still cannot confidently follow up."
      >
        <WebsitePainGrid />
      </Section>
      <Section
        id="website-process"
        tone="dark"
        eyebrow="Connected website workflow"
        title="Build the public experience and the follow-up system in the same place."
        description="Brand pages, campaign landing pages, signup forms, QR destinations, promo delivery, and reporting move together instead of becoming separate projects."
      >
        <WebsiteProcess />
      </Section>
      <Section
        id="website-builder"
        className="confidence-section"
        eyebrow="Website builder"
        title="Compete with the standalone website-builder need, then go further."
        description="B4 can support the polished public website job while making sure the site is part of acquisition, retention, campaign execution, and proof."
      >
        <WebsiteProofGrid />
      </Section>
      <SharedCta />
    </>
  );
}

function ChannelsPage() {
  return (
    <>
      <PageHero
        eyebrow="Channels"
        title="Send the right message without rebuilding the campaign for every channel."
        description="Email, social, QR redirects, landing pages, and promo signup forms stay connected to the same audience, locations, approvals, and reporting."
        dark
      />
      <Section
        id="channels"
        tone="dark"
        eyebrow="Every channel, one launch room"
        title="Each channel has a job. The campaign should still feel like one campaign."
        description="Teams can choose the channel the moment needs without creating another disconnected tool, list, or reporting problem."
      >
        <ChannelGrid />
      </Section>
      <SharedCta />
    </>
  );
}

function CompliancePage() {
  return (
    <>
      <PageHero
        eyebrow="Compliance"
        title="Checklists turn launch plans into location-level accountability."
        description="B4 helps teams schedule recurring operational tasks, monitor completion, catch misses, and report compliance by location before campaign execution turns into customer frustration."
      />
      <Section
        id="why-compliance"
        eyebrow="Why it matters"
        title="The most expensive campaign mistakes often happen after marketing says it is ready."
        description="A table tent is not useful if it never reaches the table. A promo is risky if staff do not know the rules. A QR campaign creates confusion if one location uses the wrong code. Compliance checklists close that gap."
      >
        <CompliancePainGrid />
      </Section>
      <Section
        id="checklist-proof"
        className="confidence-section"
        eyebrow="Product screenshot"
        title="Managers get a live compliance view instead of chasing updates."
        description="The checklist workspace shows compliance rate, on-time work, due or missed tasks, live task counts, task monitor, reporting, and the current checklist window for the selected location."
      >
        <ComplianceScreenshot />
      </Section>
      <Section
        id="checklist-process"
        tone="dark"
        eyebrow="Checklist workflow"
        title="Create the standard once. Make every location prove the work."
        description="Templates, schedules, task windows, priorities, location assignment, task monitor, completion states, and reports give managers a shared operating rhythm."
      >
        <ComplianceProcess />
      </Section>
      <Section
        id="compliance-reporting"
        className="confidence-section"
        eyebrow="Reporting"
        title="Managers can see what was done, missed, excused, or reopened."
        description="Compliance reports make operational readiness visible by task and location, so teams can coach the right store, adjust the checklist, and avoid arguing from memory."
      >
        <ComplianceGrid />
      </Section>
      <SharedCta />
    </>
  );
}

function ComplianceScreenshot() {
  const highlights = [
    'Dashboard, task monitor, and compliance report stay in the same workspace.',
    'Compliance, on-time, due or missed, and list counts are visible before the campaign moment.',
    'Location and time-period controls help managers review the work that matters right now.',
  ];

  return (
    <div className="screenshot-proof">
      <figure>
        <img src="/product/checklists-dashboard.png" alt="B4 checklist dashboard showing compliance metrics and task status" />
        <figcaption>Checklist dashboard for a selected brand location.</figcaption>
      </figure>
      <div className="screenshot-proof-notes" aria-label="Checklist screenshot highlights">
        {highlights.map((highlight) => (
          <article key={highlight}>
            <CheckCircle2 size={20} />
            <p>{highlight}</p>
          </article>
        ))}
      </div>
    </div>
  );
}

function WorkflowPage() {
  return (
    <>
      <PageHero
        eyebrow="Workflow"
        title="Give every campaign a calm, visible path from idea to result."
        description="Teams should not need a spreadsheet, a developer, and three dashboards just to answer whether a promotion is ready."
      />
      <WorkflowSection steps={workflowSteps} />
      <SharedCta />
    </>
  );
}

function ProofPage() {
  return (
    <>
      <PageHero
        eyebrow="Product proof"
        title="The workflow catches the details that usually cause the fire drill."
        description="Location scope, public URLs, embed code, reusable QR routing, and scan results stay visible in the same campaign workspace."
      />
      <Section
        id="proof"
        eyebrow="Built around real operator jobs"
        title="Launch details are visible before and after the campaign."
        description="Screenshots from the current B4 workflow show public distribution and reusable QR routing tied back to campaign activity."
      >
        <ProductGallery />
      </Section>
      <Section
        id="case-studies"
        eyebrow="Case studies"
        title="Proof stories you can replace with real client names later."
        description="Each story follows the same pattern: a physical touchpoint creates a scan, the landing page earns a signup, the promo rewards the guest, and B4 keeps the new member ready for the next campaign."
      >
        <CaseStudyGrid />
      </Section>
      <SharedCta />
    </>
  );
}

function CaseStudyPage({ slug }: { slug: string }) {
  const study = caseStudies.find((item) => item.slug === slug) ?? caseStudies[0];

  return (
    <>
      <PageHero
        eyebrow={`${study.category} / ${study.clientName}`}
        title={study.title}
        description={study.shortDescription}
      />
      <section className="case-summary-section">
        <div className="section-inner case-summary-grid">
          <article>
            <p className="eyebrow">Problem</p>
            <h2>{study.problem}</h2>
          </article>
          <article>
            <p className="eyebrow">How B4 helps</p>
            <h2>{study.outcome}</h2>
          </article>
        </div>
      </section>
      <section className="case-flow-section">
        <div className="section-inner">
          <div className="section-heading">
            <p className="eyebrow">Process proof</p>
            <h2>From scan to member record.</h2>
            <p>This is the story the Proof section needs to tell: B4 is not only a page builder or a QR tool. It connects the whole acquisition moment.</p>
          </div>
          <CaseFlow study={study} />
        </div>
      </section>
      <section className="case-proof-section">
        <div className="section-inner">
          <div className="section-heading compact">
            <p className="eyebrow">What this proves</p>
            <h2>Why the process matters to the client.</h2>
          </div>
          <div className="case-proof-grid">
            {study.proofPoints.map((point) => (
              <article key={point}>
                <CheckCircle2 size={20} />
                <p>{point}</p>
              </article>
            ))}
          </div>
          <div className="case-nav-row">
            <a className="button button-secondary button-on-light" href="/proof">
              Back to proof
            </a>
            <a className="button button-primary" href="/contact">
              <span>Talk through this flow</span>
              <ArrowRight size={18} />
            </a>
          </div>
        </div>
      </section>
    </>
  );
}

function ContactPage() {
  return (
    <>
      <PageHero
        eyebrow="Contact"
        title="Ready to stop treating every campaign like a rescue mission?"
        description="Talk through how your team currently launches promotions, where the anxiety shows up, and what launch confidence should look like for your brands."
        dark
      />
      <section className="contact-section">
        <div className="section-inner contact-grid">
          <article>
            <p className="eyebrow">Start here</p>
            <h2>Schedule a platform walkthrough.</h2>
            <p>Bring the last campaign that felt messier than it should have. B4 is easiest to understand when the conversation starts with a real launch problem.</p>
            <a className="button button-primary" href="mailto:info@beyond4.com?subject=B4%20platform%20walkthrough">
              <span>Email info@beyond4.com</span>
              <ArrowRight size={18} />
            </a>
          </article>
          <article>
            <p className="eyebrow">Good fit</p>
            <h2>Teams with recurring promotion pressure.</h2>
            <p>Multi-location restaurants, retail, membership, and brand teams that need public pages, QR codes, audience rules, approvals, and reporting to stay connected.</p>
          </article>
        </div>
      </section>
    </>
  );
}

function PageHero({ eyebrow, title, description, dark = false }: { eyebrow: string; title: string; description: string; dark?: boolean }) {
  return (
    <section className={dark ? 'page-hero page-hero-dark' : 'page-hero'}>
      <div className="section-inner">
        <p className="eyebrow">{eyebrow}</p>
        <h1>{title}</h1>
        <p>{description}</p>
      </div>
    </section>
  );
}

function CaseStudyGrid() {
  return (
    <div className="case-study-grid">
      {caseStudies.map((study) => (
        <a className="case-study-card" href={`/proof/${study.slug}`} key={study.slug}>
          <span>{study.category}</span>
          <strong>{study.clientName}</strong>
          <h3>{study.title}</h3>
          <p>{study.shortDescription}</p>
          <em>
            View process
            <ArrowRight size={17} />
          </em>
        </a>
      ))}
    </div>
  );
}

function CaseFlow({ study }: { study: CaseStudy }) {
  return (
    <div className="case-flow">
      {study.flow.map((step) => (
        <article key={step.label}>
          <span>{step.label}</span>
          <h3>{step.title}</h3>
          <p>{step.description}</p>
        </article>
      ))}
    </div>
  );
}

function ChannelGrid() {
  return (
    <div className="channel-grid">
      {channels.map((channel) => {
        const Icon = channel.icon;
        return (
          <article className="channel-card" key={channel.title}>
            <span className="icon-badge icon-badge-light">
              <Icon size={22} />
            </span>
            <h3>{channel.title}</h3>
            <p>{channel.description}</p>
          </article>
        );
      })}
    </div>
  );
}

function ConfidenceGrid() {
  return (
    <div className="confidence-grid">
      <article>
        <MapPin size={24} />
        <h3>Location mistakes stop being normal</h3>
        <p>Teams can target the right stores, route guests correctly, and avoid one-size-fits-all promotions that frustrate operators.</p>
      </article>
      <article>
        <CheckCircle2 size={24} />
        <h3>Approvals mean something</h3>
        <p>Campaign readiness is visible before launch, so teams are not relying on memory, screenshots, or last-minute Slack threads.</p>
      </article>
      <article>
        <BarChart3 size={24} />
        <h3>Results are easier to defend</h3>
        <p>Scans, signups, clicks, and member actions connect back to the campaign, making performance conversations clearer.</p>
      </article>
    </div>
  );
}

function CapabilityList() {
  return (
    <div className="capability-list">
      {[
        { label: 'Brand and location workspaces', icon: Building2 },
        { label: 'Member profiles and activity', icon: UsersRound },
        { label: 'Email template and asset management', icon: Mail },
        { label: 'Website builder, landing pages, and public forms', icon: Globe2 },
        { label: 'Reusable QR codes and redirects', icon: QrCode },
        { label: 'Compliance and operator checklists', icon: FileText },
        { label: 'Campaign reports and member actions', icon: BarChart3 },
        { label: 'Shared campaign lifecycle controls', icon: Layers3 },
        { label: 'Launch readiness before go-live', icon: CheckCircle2 },
      ].map(({ label, icon: Icon }) => (
        <div className="capability-row" key={label}>
          <Icon size={19} />
          <span>{label}</span>
        </div>
      ))}
    </div>
  );
}

function WebsiteProofGrid() {
  const items = [
    {
      icon: Globe2,
      title: 'Brand websites',
      description: 'Publish polished public pages for the brand, locations, menus, offers, events, or campaigns without separating the site from marketing operations.',
    },
    {
      icon: QrCode,
      title: 'QR-ready landing pages',
      description: 'Send table tents, posters, receipts, and event signage to focused pages instead of a generic homepage that loses the guest.',
    },
    {
      icon: UsersRound,
      title: 'Signup forms that create usable members',
      description: 'Capture permission, source, location, and preference context so the guest can receive the right follow-up.',
    },
    {
      icon: MapPin,
      title: 'Location-aware content',
      description: 'Keep store-specific offers, local campaigns, and public destinations aligned with the places guests actually visit.',
    },
    {
      icon: Mail,
      title: 'Promo follow-up',
      description: 'Turn a form submission into a welcome offer, bounce-back promo, or future campaign audience instead of a dead-end lead.',
    },
    {
      icon: BarChart3,
      title: 'Website proof',
      description: 'Show scans, signups, campaign responses, and member activity so the website is measured by business outcomes, not only page views.',
    },
  ];

  return (
    <div className="feature-grid">
      {items.map(({ icon: Icon, title, description }) => (
        <article className="feature-card" key={title}>
          <span className="icon-badge">
            <Icon size={22} />
          </span>
          <h3>{title}</h3>
          <p>{description}</p>
        </article>
      ))}
    </div>
  );
}

function WebsitePainGrid() {
  const items = [
    ['The website looks good, but the signup form does not feed the campaign list with useful context.', 'B4 turns form submissions into member records with source, permission, location, and follow-up readiness.'],
    ['A printed QR code sends guests to a homepage where the offer is hard to find.', 'B4 routes each physical touchpoint to a focused page that matches the moment and the location.'],
    ['A manager wants a local page or offer, but every change feels like a separate web project.', 'B4 keeps location pages, campaign pages, and public forms close to the marketing workflow.'],
    ['The team knows the website got traffic, but cannot prove whether it created signups, offers, or return visits.', 'B4 connects website activity back to scans, members, campaigns, and reports.'],
  ];

  return (
    <div className="pain-grid">
      {items.map(([problem, solution]) => (
        <article className="pain-card" key={problem}>
          <p className="problem">{problem}</p>
          <ArrowRight size={18} />
          <p className="solution">{solution}</p>
        </article>
      ))}
    </div>
  );
}

function WebsiteProcess() {
  const steps = [
    ['01', 'Create the public page', 'Build the brand, location, campaign, or offer page guests will actually see.'],
    ['02', 'Add the signup or offer', 'Use forms and promo calls to action that match the guest moment instead of a generic contact form.'],
    ['03', 'Connect QR and channels', 'Point table tents, receipts, event signs, email, and social posts to the right public destination.'],
    ['04', 'Capture the member', 'Store source, permission, location, and preference details so the guest is ready for follow-up.'],
    ['05', 'Report the outcome', 'Review scans, signups, campaign response, and follow-up value from the same system.'],
  ];

  return (
    <div className="case-flow case-flow-dark">
      {steps.map(([label, title, description]) => (
        <article key={label}>
          <span>{label}</span>
          <h3>{title}</h3>
          <p>{description}</p>
        </article>
      ))}
    </div>
  );
}

function ComplianceGrid() {
  const items = [
    {
      icon: ClipboardCheck,
      title: 'Checklist templates',
      description: 'Define repeatable tasks like placing table tents, confirming offer rules, checking QR codes, or briefing staff.',
    },
    {
      icon: CalendarDays,
      title: 'Schedules and windows',
      description: 'Assign checklists to the right days, times, priorities, and locations so work is expected before the campaign moment.',
    },
    {
      icon: MapPin,
      title: 'Location accountability',
      description: 'Track completion by store instead of assuming every location executed the same plan.',
    },
    {
      icon: BarChart3,
      title: 'Compliance reporting',
      description: 'See compliance rate, missed tasks, late completions, and location-level patterns that need attention.',
    },
    {
      icon: ShieldCheck,
      title: 'Manager review',
      description: 'Reopen, excuse, or reject task states when the real-world situation needs a controlled exception.',
    },
    {
      icon: CheckCircle2,
      title: 'Launch confidence',
      description: 'Give marketing and operations the same answer when they ask whether the campaign is truly ready.',
    },
  ];

  return (
    <div className="compliance-grid">
      {items.map(({ icon: Icon, title, description }) => (
        <article className="feature-card" key={title}>
          <span className="icon-badge">
            <Icon size={22} />
          </span>
          <h3>{title}</h3>
          <p>{description}</p>
        </article>
      ))}
    </div>
  );
}

function CompliancePainGrid() {
  const items = [
    ['The QR code is printed, but no one confirms it scans to the right page before service starts.', 'Checklist task: scan and verify the public page at each location.'],
    ['The promo is live, but staff are not sure what guests should receive after signup.', 'Checklist task: confirm offer rules and staff talking points.'],
    ['The table tents arrive, but one location forgets to place them until after the lunch rush.', 'Checklist task: placement confirmation with a due window by location.'],
    ['The campaign ends, but nobody knows whether poor results came from the offer or poor execution.', 'Checklist report: compare compliance with campaign performance.'],
  ];

  return (
    <div className="pain-grid">
      {items.map(([problem, solution]) => (
        <article className="pain-card" key={problem}>
          <p className="problem">{problem}</p>
          <ArrowRight size={18} />
          <p className="solution">{solution}</p>
        </article>
      ))}
    </div>
  );
}

function ComplianceProcess() {
  const steps = [
    ['01', 'Build the template', 'Create the operational checklist once with the exact tasks each location must complete.'],
    ['02', 'Schedule by location', 'Choose recurrence, timing, priority, and the stores responsible for the work.'],
    ['03', 'Monitor task state', 'Watch tasks move from scheduled to due, completed, late, missed, excused, reopened, or rejected.'],
    ['04', 'Review exceptions', 'Managers can handle real-world exceptions without losing the audit trail.'],
    ['05', 'Report compliance', 'See which tasks and locations are helping or hurting campaign readiness.'],
  ];

  return (
    <div className="case-flow case-flow-dark">
      {steps.map(([label, title, description]) => (
        <article key={label}>
          <span>{label}</span>
          <h3>{title}</h3>
          <p>{description}</p>
        </article>
      ))}
    </div>
  );
}

function SharedCta() {
  return (
    <CtaBand
      title="Ready to stop treating every campaign like a rescue mission?"
      description="Use B4 to give brand, ops, and marketing teams one shared place to prepare, publish, route, and prove every promotion."
      action={<><span>Schedule a platform walkthrough</span><ArrowRight size={18} /></>}
    />
  );
}
